import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconSearch,
} from "@tabler/icons-react";
import {
  prefetchClinicalCareGuidelineDetail,
  useClinicalCareGuidelineDetail,
  useClinicalCareGuidelinesList,
} from "../../hooks/queries";
import type {
  ClinicalCareGuidelineListItem,
  ClinicalCareGuidelineSort,
} from "../../types/clinical-care-guidelines";
import {
  listItemAuthor,
  listItemRevisionNumber,
} from "../../utils/clinical-care-guidelines";
import ModuleBackButton from "../Shared/ModuleBackButton";
import GuidelineMarkdown from "./GuidelineMarkdown";

const SEARCH_DEBOUNCE_MS = 400;

const GuidelineRow: React.FC<{
  item: ClinicalCareGuidelineListItem;
  selected: boolean;
  onSelect: () => void;
  onPrefetch: () => void;
}> = ({ item, selected, onSelect, onPrefetch }) => (
  <button
    type="button"
    onClick={onSelect}
    onMouseEnter={onPrefetch}
    onFocus={onPrefetch}
    className={`w-full border-b border-slate-100 px-3 py-2.5 text-left transition-colors ${
      selected ? "bg-emerald-50/80" : "bg-white hover:bg-slate-50"
    }`}
    style={{
      borderLeft: selected ? "4px solid #34d399" : "4px solid transparent",
    }}
  >
    <p className="min-w-0 truncate text-sm font-semibold uppercase text-slate-800">
      {item.condition}
    </p>
    <p className="mt-0.5 truncate text-xs text-slate-500">
      Rev {listItemRevisionNumber(item)} | {listItemAuthor(item)}
    </p>
  </button>
);

const ClinicalCareGuidelinesWorkspace: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [sort, setSort] = useState<ClinicalCareGuidelineSort>("conditionAsc");
  const [category, setCategory] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchInput === debouncedSearch) {
      setIsDebouncing(false);
      return undefined;
    }
    setIsDebouncing(true);
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
      setIsDebouncing(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput, debouncedSearch]);

  useEffect(() => {
    setSelectedId(null);
  }, [debouncedSearch, sort, category]);

  const listQuery = useClinicalCareGuidelinesList({
    search: debouncedSearch,
    category,
    sort,
  });

  const items = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.guidelines) ?? [],
    [listQuery.data]
  );
  const categories = listQuery.data?.pages[0]?.categories ?? [];

  useEffect(() => {
    if (!selectedId && items[0]?.id) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  const detailQuery = useClinicalCareGuidelineDetail(selectedId);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = listQuery;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "120px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [items.length, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const guideline = detailQuery.data ?? null;
  const isFirstLoad = listQuery.isPending && items.length === 0;
  const isSearchPending = isDebouncing;
  const isUpdating =
    listQuery.isFetching &&
    !listQuery.isPending &&
    !isFetchingNextPage &&
    !isDebouncing;
  const listFailed =
    listQuery.isError ||
    Boolean(
      listQuery.isSuccess &&
        listQuery.data?.pages.some((page) => page.success === false) &&
        items.length === 0
    );

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-slate-100">
      <header className="relative flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <ModuleBackButton />
          <img
            src="/bulwark_logo-new.png"
            alt="Bulwark Health"
            className="h-10 w-auto object-contain"
          />
        </div>
        <div className="pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-center gap-2">
          <img
            src="/ai-bulwark-logo.svg"
            alt=""
            aria-hidden
            className="h-7 w-auto shrink-0 object-contain"
          />
          <h1 className="text-lg font-semibold text-slate-800">
            Clinical Care Guidelines
          </h1>
        </div>
        <div className="h-10 w-[52px] shrink-0" aria-hidden />
      </header>

      <div className="flex min-h-0 flex-1 gap-3 p-3">
        {sidebarHidden ? (
          <button
            type="button"
            onClick={() => setSidebarHidden(false)}
            className="flex w-9 shrink-0 flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white py-4 text-xs font-semibold tracking-wide text-slate-700 shadow-sm"
          >
            <IconLayoutSidebarLeftExpand size={18} stroke={1.75} aria-hidden />
            <span className="[writing-mode:vertical-rl]">Show Guidelines</span>
          </button>
        ) : (
          <aside className="flex w-[360px] shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
              <h2 className="text-sm font-semibold text-slate-800">Guidelines</h2>
              <button
                type="button"
                onClick={() => setSidebarHidden(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <IconLayoutSidebarLeftCollapse size={16} stroke={1.75} aria-hidden />
                Hide
              </button>
            </div>

            <div className="space-y-2 border-b border-slate-100 px-3 py-2">
              <div className="relative">
                <IconSearch
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search"
                  className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-800 outline-none focus:border-indigo-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500" htmlFor="guideline-sort">
                  Sort
                </label>
                <select
                  id="guideline-sort"
                  value={sort}
                  onChange={(event) =>
                    setSort(event.target.value as ClinicalCareGuidelineSort)
                  }
                  className="h-8 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
                >
                  <option value="conditionAsc">A–Z</option>
                  <option value="conditionDesc">Z–A</option>
                </select>
              </div>
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {categories.map((name) => {
                    const active = category === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setCategory(active ? "" : name)}
                        className={`rounded-full border px-2 py-0.5 text-[11px] ${
                          active
                            ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-white"
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {isFirstLoad || isSearchPending ? (
                <p className="px-3 py-6 text-center text-sm text-slate-500">
                  {isSearchPending ? "Searching..." : "Loading records..."}
                </p>
              ) : listFailed ? (
                <p className="px-3 py-6 text-center text-sm text-red-600">
                  Failed to load clinical care guidelines
                </p>
              ) : items.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-slate-500">
                  No clinical care guidelines found.
                </p>
              ) : (
                <>
                  {isUpdating && (
                    <p className="px-3 py-2 text-center text-xs text-slate-500">
                      Updating results...
                    </p>
                  )}
                  {items.map((item) => (
                    <GuidelineRow
                      key={item.id}
                      item={item}
                      selected={item.id === selectedId}
                      onSelect={() => setSelectedId(item.id)}
                      onPrefetch={() => prefetchClinicalCareGuidelineDetail(item.id)}
                    />
                  ))}
                  <div ref={sentinelRef} className="h-4" />
                  {isFetchingNextPage && (
                    <p className="px-3 py-3 text-center text-xs text-slate-500">
                      Loading more records...
                    </p>
                  )}
                </>
              )}
            </div>
          </aside>
        )}

        <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          {!selectedId && items.length === 0 && !isFirstLoad && !listFailed ? (
            <div className="flex h-full items-center justify-center">
              <div className="rounded-lg border border-slate-200 bg-white px-8 py-10 text-sm text-slate-500 shadow-sm">
                No clinical care guidelines found.
              </div>
            </div>
          ) : listFailed && !guideline ? (
            <div className="flex h-full items-center justify-center text-sm text-red-600">
              Failed to load clinical care guidelines
            </div>
          ) : !selectedId || (detailQuery.isPending && !guideline) ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              Loading records...
            </div>
          ) : detailQuery.isError || !guideline ? (
            <div className="flex h-full items-center justify-center text-sm text-red-600">
              Failed to load clinical care guidelines
            </div>
          ) : (
            <>
              <div className="border-b border-emerald-200 bg-emerald-50/80 px-4 py-3">
                <h2 className="truncate text-base font-semibold uppercase text-slate-900">
                  {guideline.condition}
                </h2>
                <p className="mt-1 text-xs text-slate-600">
                  Revision #
                  {guideline.latestRevision?.revisionNumber ??
                    guideline.latestRevisionNumber}
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-auto p-4">
                <GuidelineMarkdown
                  markdown={guideline.latestRevision?.contentMarkdown}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ClinicalCareGuidelinesWorkspace;
